import { IntlShape } from 'react-intl';
import { ClientIdentificationPanelDto, MissingItemDto, TierCode } from 'types/clientIdentification';
import { t, translations } from 'utils/getTranslations';

/** What a client item or an answer is called on the page, for a panel key; the key itself for one the catalog lacks. */
export function itemLabel(key: string): string {
    const id = `client-identification.item.${key}`;
    return id in translations ? t(id) : key;
}

/**
 * The client items the panel reports missing when blank: every one but the registration date and authority, the
 * principal place of business and the personal code, and the home address at Simplified, which asks it only where
 * available. Occupation counts as answered by an employment status that stands in for it.
 */
export function isRequiredClientItem(key: string, tierCode: TierCode): boolean {
    return REQUIRED_CLIENT_ITEMS.has(key) && !(key === 'HOME_ADDRESS' && tierCode === 'SIMPLIFIED');
}

const REQUIRED_CLIENT_ITEMS = new Set([
    'NAME_AND_LEGAL_FORM',
    'REGISTRATION_CODE',
    'REGISTRATION_ADDRESS',
    'NATURE_OF_BUSINESS',
    'NAMES',
    'DATE_OF_BIRTH',
    'NATIONALITIES',
    'HOME_ADDRESS',
    'OCCUPATION',
]);

/** A missing item as the review and the receipt dialog name it: the item, and the person it is asked of. */
export function missingItemLabel(item: MissingItemDto, panel: ClientIdentificationPanelDto): string {
    // A "yes" with nothing on who and which function is reported as the political exposure item too.
    const label = itemLabel(item.key === 'PEP' && panel.pep.answer === 'YES' ? 'PEP_DETAILS' : item.key);
    const person = item.partyId === null ? undefined : panel.people.find((p) => p.partyId === item.partyId);
    return person && person.partyId !== panel.clientPartyId
        ? t('client-identification.item-of-person', { item: label, name: person.name })
        : label;
}

/** Every gap the panel reports, items first, then the mandatory roles nobody is listed in. */
export function missingLabels(panel: ClientIdentificationPanelDto): string[] {
    return [
        ...panel.missingItems.map((item) => missingItemLabel(item, panel)),
        ...panel.missingMandatoryRoles.map((role) =>
            t('client-identification.no-role-listed', { role: role.namePlural })
        ),
    ];
}

/** "{n} items are missing: a; b." for the receipt dialog; nothing while nothing is missing. */
export function missingSummary(panel: ClientIdentificationPanelDto, intl: IntlShape): string | null {
    if (panel.missingCount === 0) {
        return null;
    }
    return intl.formatMessage(
        { id: 'client-identification.receive-missing' },
        { count: panel.missingCount, items: intl.formatList(missingLabels(panel), { type: 'unit' }) }
    );
}
